import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { AlertCircle, Bell, CheckCircle2, Loader2, MessageSquare, Plus, Play, Search, Trash2, X } from 'lucide-react';
import {
  commentTask,
  completeTask,
  createTask,
  deleteTask,
  getNotifications,
  getTasks,
  getUsers,
  markNotificationRead,
  startTask,
} from '../../services/api';

const statusMeta = {
  pending: { label: 'Ожидает', className: 'bg-slate-500/10 text-slate-300 border-white/10' },
  in_progress: { label: 'В работе', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  completed: { label: 'Завершена', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
};

const TasksPage = () => {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch { return null; }
  }, []);
  const isAdmin = user?.role === 'admin';

  // Состояния данных
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [managers, setManagers] = useState([]);

  // Пагинация
  const [tasksMeta, setTasksMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [notificationsMeta, setNotificationsMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [tasksPage, setTasksPage] = useState(1);
  const [notificationsPage, setNotificationsPage] = useState(1);

  // Индикаторы загрузки
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [commentLoadingId, setCommentLoadingId] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  // Формы и поиск
  const [commentDrafts, setCommentDrafts] = useState({});
  const [form, setForm] = useState({ title: '', description: '', assigned_to: '', deadline: '' });
  const [searchQuery, setSearchQuery] = useState('');

  // ОСНОВНАЯ ФУНКЦИЯ ЗАГРУЗКИ
  const loadData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const [tasksResp, notificationsResp] = await Promise.all([
        getTasks({ page: tasksPage, per_page: 6 }),
        getNotifications({ page: notificationsPage, per_page: 8 }),
      ]);

      setTasks(tasksResp.data || []);
      setTasksMeta(tasksResp.meta || { current_page: 1, last_page: 1, total: 0 });
      setNotifications(notificationsResp.data || []);
      setNotificationsMeta(notificationsResp.meta || { current_page: 1, last_page: 1, total: 0 });
      setUnreadCount(notificationsResp.unread_count || 0);
    } catch (err) {
      console.error("Ошибка загрузки данных:", err);
    } finally {
      setLoading(false);
    }
  }, [tasksPage, notificationsPage]);

  // Загрузка списка менеджеров (только 1 раз при входе админа)
  useEffect(() => {
    if (isAdmin) {
      getUsers().then(users => setManagers(users.filter(u => u.role === 'manager')));
    }
  }, [isAdmin]);

  // Эффект первичной загрузки и пагинации
  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // Эффект фонового обновления (раз в 30 секунд, только если вкладка активна)
  useEffect(() => {
    const autoUpdate = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadData(true);
      }
    }, 30000); // 30 секунд оптимально для задач

    return () => clearInterval(autoUpdate);
  }, [loadData]);

  const filteredTasks = useMemo(() => {
    if (!searchQuery.trim()) return tasks;
    const q = searchQuery.trim().toLowerCase();
    return tasks.filter(
      (t) =>
        (t.title || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.assignee?.name || '').toLowerCase().includes(q)
    );
  }, [tasks, searchQuery]);

  // ОБРАБОТЧИКИ СОБЫТИЙ
  const handleCreateTask = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTask(form);
      setForm({ title: '', description: '', assigned_to: '', deadline: '' });
      await loadData(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Не удалось создать задачу');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTaskAction = async (action, taskId) => {
    setActionLoadingId(taskId);
    try {
      if (action === 'start') await startTask(taskId);
      if (action === 'complete') await completeTask(taskId);
      await loadData(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Ошибка действия');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleComment = async (taskId) => {
    const text = (commentDrafts[taskId] || '').trim();
    if (!text) return;
    setCommentLoadingId(taskId);
    try {
      await commentTask(taskId, text);
      setCommentDrafts(prev => ({ ...prev, [taskId]: '' }));
      await loadData(true);
    } catch (err) {
      alert('Ошибка при добавлении комментария');
    } finally {
      setCommentLoadingId(null);
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Удалить задачу «${task.title}»?`)) return;

    setDeleteLoadingId(task.id);
    try {
      await deleteTask(task.id);
      await loadData(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Не удалось удалить задачу');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleReadNotification = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error(err); }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-4 sm:p-6 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Хедер */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black tracking-tighter text-white flex items-center gap-3 italic uppercase">
            Задачи
            </h1>
            <div className="flex items-center gap-2 mt-2 uppercase text-[10px] font-black tracking-[0.3em] text-indigo-400">
              <div className="h-1 w-6 bg-indigo-500 rounded-full" /> Система контроля
            </div>
          </div>
       <div className="flex flex-wrap items-end gap-3">
  {/* Блок поиска */}
  <div className="relative flex-grow min-w-0 md:min-w-[220px]">
    <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
      Поиск по задачам
    </label>
    <div className="relative h-[42px]">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={16} />
      <input
        type="text"
        placeholder="Заголовок, описание, исполнитель..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full h-full bg-[#1e293b] border border-white/10 rounded-xl pl-10 pr-10 text-xs text-slate-200 placeholder:text-slate-500 focus:border-indigo-500 outline-none transition-all"
      />
      {searchQuery && (
        <X 
          size={14} 
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-slate-500 hover:text-white" 
          onClick={() => setSearchQuery('')} 
        />
      )}
    </div>
  </div>

  {/* Блок уведомлений */}
  <div className="h-[42px] px-4 rounded-xl bg-white/5 border border-white/10 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 whitespace-nowrap">
    <Bell 
      size={14} 
      className={unreadCount > 0 ? "text-rose-400 animate-pulse" : "text-slate-500"} 
    />
    <span className="text-slate-400">Уведомления:</span>
    <span className={unreadCount > 0 ? "text-white" : "text-slate-500"}>{unreadCount}</span>
  </div>
</div>
        </div>

        {isAdmin && (
          <form onSubmit={handleCreateTask} className="bg-[#1e293b]/40 border border-white/5 rounded-[2rem] p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <input required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Заголовок" className="bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition-colors" />
            <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Описание" className="bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition-colors" />
            <select required value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} className="bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none cursor-pointer">
              <option value="">Менеджер</option>
              {managers.map(m => <option key={m.id} value={m.id} className="bg-slate-900">{m.name}</option>)}
            </select>
            <input required type="datetime-local" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))} className="bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none" />
            <button disabled={submitting} className="h-12 sm:h-14 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2.5 px-5 transition-all active:scale-95">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
              Создать
            </button>
          </form>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Список задач */}
          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="flex min-h-[400px] items-center justify-center rounded-[2rem] bg-[#1e293b]/20 border border-white/5">
                <Loader2 className="animate-spin text-indigo-500" size={40} />
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="text-center py-20 bg-white/5 rounded-[2rem] border border-dashed border-white/10 text-slate-500 uppercase text-xs font-black tracking-widest">
                {searchQuery ? 'Ничего не найдено' : 'Нет активных задач'}
              </div>
            ) : (
              filteredTasks.map((task) => {
                const meta = statusMeta[task.status] || statusMeta.pending;
                const isMyTask = user?.id === task.assigned_to;

                return (
                  <div key={task.id} className="group relative bg-[#1e293b]/35 border border-white/5 hover:border-white/10 rounded-[2rem] p-6 space-y-4 transition-all">
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleDeleteTask(task)}
                        disabled={deleteLoadingId === task.id}
                        title="Удалить задачу"
                        className="absolute top-5 right-5 p-2 text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all disabled:opacity-40 z-10"
                      >
                        {deleteLoadingId === task.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    )}
                    <div className="flex items-start justify-between gap-4 pr-10">
                      <div className="space-y-1">
                        <h3 className="text-xl font-black text-white group-hover:text-indigo-300 transition-colors">{task.title}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">{task.description || 'Без описания'}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2">
                          <span className="text-[10px] text-slate-500 uppercase font-black tracking-wider">Исполнитель: {task.assignee?.name || '—'}</span>
                          <span className="text-[10px] text-indigo-400 uppercase font-black tracking-wider">Срок: {new Date(task.deadline).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className={`px-3 py-1 rounded-full border text-[9px] uppercase font-black tracking-wider shadow-lg ${meta.className}`}>
                          {meta.label}
                        </span>
                        {task.is_overdue && task.status !== 'completed' && (
                          <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 text-[8px] uppercase font-black rounded border border-rose-500/30">Просрочено</span>
                        )}
                      </div>
                    </div>

                    {/* Кнопки действий для исполнителя */}
                    {isMyTask && task.status !== 'completed' && (
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleTaskAction('start', task.id)}
                          disabled={task.status !== 'pending' || actionLoadingId === task.id}
                          className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-[10px] uppercase font-black text-slate-900 flex items-center gap-2 transition-all"
                        >
                          {actionLoadingId === task.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} fill="currentColor" />}
                          В работу
                        </button>
                        <button
                          onClick={() => handleTaskAction('complete', task.id)}
                          disabled={actionLoadingId === task.id}
                          className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-[10px] uppercase font-black text-white flex items-center gap-2 transition-all"
                        >
                          {actionLoadingId === task.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                          Завершить
                        </button>
                      </div>
                    )}

                    {/* Комментарии */}
                    <div className="pt-4 border-t border-white/5 space-y-3">
                      <p className="text-[9px] uppercase font-black tracking-[0.2em] text-slate-500">Обсуждение</p>
                      <div className="space-y-2 max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                        {(task.comments || []).map((c) => (
                          <div key={c.id} className="bg-black/20 rounded-xl px-3 py-2 text-xs border border-white/5">
                            <span className="font-black text-indigo-400 mr-2">{c.user?.name}:</span>
                            <span className="text-slate-300">{c.comment}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          value={commentDrafts[task.id] || ''}
                          onChange={e => setCommentDrafts(p => ({ ...p, [task.id]: e.target.value }))}
                          placeholder="Ваше сообщение..."
                          onKeyDown={(e) => e.key === 'Enter' && handleComment(task.id)}
                          className="flex-1 bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2 text-xs outline-none focus:border-indigo-500 transition-all"
                        />
                        <button
                          onClick={() => handleComment(task.id)}
                          disabled={commentLoadingId === task.id}
                          className="p-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 transition-colors"
                        >
                          {commentLoadingId === task.id ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Пагинация задач */}
            <div className="flex items-center justify-between pt-4">
              <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Всего: {tasksMeta.total}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setTasksPage(p => Math.max(1, p - 1))} disabled={tasksPage <= 1} className="p-2 rounded-lg bg-white/5 border border-white/10 disabled:opacity-20 hover:bg-white/10 transition-colors">Назад</button>
                <span className="text-xs font-black px-3">{tasksMeta.current_page} / {tasksMeta.last_page}</span>
                <button onClick={() => setTasksPage(p => Math.min(tasksMeta.last_page, p + 1))} disabled={tasksPage >= tasksMeta.last_page} className="p-2 rounded-lg bg-white/5 border border-white/10 disabled:opacity-20 hover:bg-white/10 transition-colors">Вперед</button>
              </div>
            </div>
          </div>

      {/* Колонка уведомлений */}
<div className="space-y-4">
  <div className="bg-[#1e293b]/35 border border-white/5 rounded-[2.5rem] p-6 sticky top-8 backdrop-blur-xl">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-[11px] uppercase font-black tracking-[0.2em] text-indigo-400 flex items-center gap-2">
        <div className="p-1.5 bg-indigo-500/10 rounded-lg">
          <AlertCircle size={14} />
        </div>
        Живая лента
      </h2>
      {/* Счетчик новых */}
      {notifications.filter(n => !n.is_read).length > 0 && (
        <span className="bg-rose-500/10 text-rose-400 text-[9px] font-black px-2 py-0.5 rounded-full border border-rose-500/20">
          NEW
        </span>
      )}
    </div>

    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar transition-all">
      {notifications.length === 0 ? (
        <div className="py-10 text-center border border-dashed border-white/5 rounded-3xl">
          <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest">Событий пока нет</p>
        </div>
      ) : (
        notifications.map((n) => (
          <button
            key={n.id}
            onClick={() => handleReadNotification(n.id)}
            className={`w-full text-left rounded-[1.25rem] border p-4 transition-all duration-300 group relative overflow-hidden ${
              n.is_read 
                ? 'bg-black/5 border-white/5 text-slate-500 hover:bg-black/10' 
                : 'bg-indigo-600/10 border-indigo-500/20 text-white shadow-lg shadow-indigo-500/5 hover:bg-indigo-600/[0.15]'
            }`}
          >
            {/* Декоративная полоска для непрочитанных */}
            {!n.is_read && (
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500" />
            )}

            <div className="flex items-start justify-between mb-1.5">
              <span className={`text-[11px] font-black uppercase tracking-tight ${n.is_read ? 'text-slate-500' : 'text-indigo-200'}`}>
                {n.title}
              </span>
              {!n.is_read && (
                <div className="h-1.5 w-1.5 bg-indigo-500 rounded-full shadow-[0_0_8px_rgba(99,102,241,0.8)] animate-pulse mt-1" />
              )}
            </div>
            <p className={`text-xs leading-relaxed ${n.is_read ? 'font-normal opacity-70' : 'font-medium'}`}>
              {n.message}
            </p>
          </button>
        ))
      )}
    </div>

    {/* Пагинация */}
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
      <div className="flex flex-col">
        <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Страница</span>
        <span className="text-[11px] font-black text-slate-300">{notificationsPage} <span className="text-slate-600">/</span> {notificationsMeta.last_page}</span>
      </div>
      <div className="flex gap-1.5">
        <button 
          onClick={() => setNotificationsPage(p => Math.max(1, p - 1))} 
          disabled={notificationsPage <= 1} 
          className="h-8 w-8 flex items-center justify-center rounded-xl bg-white/5 text-[10px] hover:bg-white/10 transition-all disabled:opacity-10 border border-white/5"
        >
          ←
        </button>
        <button 
          onClick={() => setNotificationsPage(p => Math.min(notificationsMeta.last_page, p + 1))} 
          disabled={notificationsPage >= notificationsMeta.last_page} 
          className="h-8 w-8 flex items-center justify-center rounded-xl bg-white/5 text-[10px] hover:bg-white/10 transition-all disabled:opacity-10 border border-white/5"
        >
          →
        </button>
      </div>
    </div>
  </div>
</div>

        </div>
      </div>
    </div>
  );
};

export default TasksPage;